import { calculateGrossMarginFromUnitPrice } from '@calculations/price/utilities/gross-margin';
import { calculateTotal } from '@calculations/price/utilities/total';
import { SnapSuggestionStatus } from '@snap-types/models/common';
import { PriceStrategy } from '@snap-types/models/enums';
import { Decimal } from '@snap-types/types/decimal';
import { WritableQuoteBuilderStateType } from '@ui/features/quote-builder/types/state';

import {
  getQuoteLineOrThrow,
  getQuotePriceOrThrow,
} from '../../utilities/action-guards';
import { OnQuantityChangeInput } from '../types';
import { buildStrategyCalculations } from '../utilities/build-strategy-calculations';

export function onQuantityChange(
  state: WritableQuoteBuilderStateType,
  params: { quoteLineUuid: string; quotePriceUuid: string; quantity: Decimal },
) {
  const quoteLine = getQuoteLineOrThrow(state, params.quoteLineUuid);
  const quotePrice = getQuotePriceOrThrow(
    state,
    params.quoteLineUuid,
    params.quotePriceUuid,
  );
  const usingCalculatedGrossMargin =
    quotePrice.automatedGrossMargin &&
    quotePrice.grossMargin?.eq(quotePrice.automatedGrossMargin);

  const usingCalculatedBaseGrossMargin =
    quotePrice.baseGrossMargin &&
    quotePrice.automatedBaseGrossMargin &&
    quotePrice.baseGrossMargin?.eq(quotePrice.automatedBaseGrossMargin);

  const usingCalculatedContributionMargin =
    quotePrice.contributionMargin &&
    quotePrice.automatedContributionMargin &&
    quotePrice.contributionMargin?.eq(quotePrice.automatedContributionMargin);

  const { priceStrategy } = quoteLine;

  const inputs = {
    state,
    quoteLine,
    quotePrice,
    quantity: params.quantity,
  } satisfies OnQuantityChangeInput;

  const calculation =
    buildStrategyCalculations('onQuantityChange')[priceStrategy];

  if (!calculation) {
    console.error(
      'No quantity calculation function found for price strategy',
      priceStrategy,
    );
    return;
  }

  const result = calculation(inputs);

  if (result.status === 'failure') {
    // @TODO(NT): Our store should be integrated with toast notifications
    console.error('Failed to calculate price', result.reason);
    return;
  }

  quotePrice.quantity = params.quantity;
  quotePrice.userQuantity = params.quantity;

  if (result.status === 'success') {
    quotePrice.calculatedUnitPrice = result.unitPrice;
    quotePrice.unitPrice = result.unitPrice;
    quotePrice.userUnitPrice = null;

    quotePrice.unitCost = result.unitCost ?? null;

    quotePrice.grossMargin = result.grossMargin ?? null;

    quotePrice.unitBurdenCost = result.unitBurdenCost ?? null;
    quotePrice.unitLaborCost = result.unitLaborCost ?? null;
    quotePrice.unitMaterialCost = result.unitMaterialCost ?? null;
    quotePrice.unitServiceCost = result.unitServiceCost ?? null;
    quotePrice.unitVariableCosts = result.unitVariableCosts ?? null;
    quotePrice.contributionMargin = result.contributionMargin ?? null;

    quotePrice.baseUnitPrice = result.baseUnitPrice ?? null;
    quotePrice.priceAdjustmentAmount = result.priceAdjustmentAmount ?? null;

    quotePrice.unitPriceAddition = result.unitPriceAddition ?? null;
    quotePrice.changesUnitPrice = result.changesUnitPrice ?? false;

    quotePrice.totalPrice = calculateTotal(result.unitPrice, params.quantity);

    switch (priceStrategy) {
      case PriceStrategy.PriceSegmentation:
      case PriceStrategy.CAPPriceSegmentation:
        // When changing quantity, the existing base gross margin remains unchanged;
        // If that value was already the calculated value, now ALL values are calculated, and the user has
        // accepted the price
        if (usingCalculatedBaseGrossMargin) {
          quotePrice.suggestedPriceStatus = SnapSuggestionStatus.ACCEPTED;
        }
        break;

      case PriceStrategy.TargetContributionMargin:
        // When changing quantity, the existing contribution margin remains unchanged;
        // If that value was already the calculated value, now ALL values are calculated, and the user has
        // accepted the price
        if (usingCalculatedContributionMargin) {
          quotePrice.suggestedPriceStatus = SnapSuggestionStatus.ACCEPTED;
        }
        break;

      case PriceStrategy.TargetGrossMargin:
      case PriceStrategy.RAFTargetGrossMargin:
        // When changing quantity, the existing gross margin remains unchanged;
        // If that value was already the calculated value, now ALL values are calculated, and the user has
        // accepted the price
        if (usingCalculatedGrossMargin) {
          quotePrice.suggestedPriceStatus = SnapSuggestionStatus.ACCEPTED;
        }
        break;
      default:
        quotePrice.suggestedPriceStatus = SnapSuggestionStatus.ACCEPTED;
    }
  }

  if (result.status === 'no-op') {
    quotePrice.totalPrice = calculateTotal(
      quotePrice.unitPrice,
      params.quantity,
    );
  }

  // Handles strategies that need to calculate a unit cost and margin, but not a unit price
  if (result.status === 'skip-price') {
    quotePrice.unitCost = result.unitCost ?? null;

    const { unitPrice } = quotePrice;

    quotePrice.totalPrice = calculateTotal(unitPrice, params.quantity);

    if (result.unitCost) {
      const grossMargin = calculateGrossMarginFromUnitPrice(
        {
          unitCost: result.unitCost,
          unitPrice,
        },
        state.calculationConfig,
      );

      quotePrice.grossMargin = grossMargin;
      quotePrice.userGrossMargin = null;
    }
  }

  const allPricesAccepted = Object.values(quoteLine.quotePrices).every(
    (quotePrice) =>
      quotePrice.suggestedPriceStatus === SnapSuggestionStatus.ACCEPTED,
  );
  quoteLine.suggestedPricesStatus = allPricesAccepted
    ? SnapSuggestionStatus.ACCEPTED
    : SnapSuggestionStatus.REJECTED;

  const allPricesAutomated = Object.values(quoteLine.quotePrices).every(
    (quotePrice) => quotePrice.automatedQuantity?.eq(quotePrice.quantity),
  );

  // A QuoteLine is automatable if ALL of the price lines started with automated values (
  // meaning they were added at the same time the quote line was added) AND are user-editable
  // values were accepted
  quoteLine.automatable =
    quoteLine.leadTimeSuggestionStatus === SnapSuggestionStatus.ACCEPTED &&
    allPricesAutomated &&
    quoteLine.suggestedPricesStatus === SnapSuggestionStatus.ACCEPTED;
}
